/**
 * Safe area detection utilities for handling notches and system navigation bars
 * Works with Capacitor StatusBar plugin to manage system UI
 */

import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// The theme the status bar currently mirrors. Kept at module level so the
// reset watcher (fullscreen exit, app resume) re-applies the active theme
// instead of a hardcoded one. Defaults match the light theme surface.
let statusBarTheme = { dark: false, backgroundColor: '#F4F4F5' };

/**
 * Configure status bar for the app
 * Handles notch/pinhole camera and system bar visibility
 */
export const configureStatusBar = async (): Promise<void> => {
  // Only configure on native platforms
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Keep the WebView below the Android status bar. This avoids relying on
    // safe-area env vars that are often 0 in Android WebView.
    await StatusBar.setOverlaysWebView({ overlay: false });

    // Style names follow the BACKGROUND they sit on: Style.Dark renders light
    // icons for dark surfaces, Style.Light renders dark icons for light ones.
    await StatusBar.setStyle({
      style: statusBarTheme.dark ? Style.Dark : Style.Light,
    });

    await StatusBar.setBackgroundColor({ color: statusBarTheme.backgroundColor });

    // Show the status bar
    await StatusBar.show();
  } catch (err) {
    console.warn('StatusBar configuration failed:', err);
  }
};

/**
 * Point the status bar at the app's resolved theme: icon contrast + bar
 * background. Called on boot and whenever the theme changes (including a
 * live OS switch while the preference is 'system').
 */
export const syncStatusBarTheme = async (
  dark: boolean,
  backgroundColor: string
): Promise<void> => {
  statusBarTheme = { dark, backgroundColor };
  await configureStatusBar();
};

/**
 * Get safe area insets
 * Returns top, right, bottom, left insets in pixels
 */
export const getSafeAreaInsets = (): Insets => {
  const root = document.documentElement;
  const styles = getComputedStyle(root);

  return {
    top: Number.parseFloat(styles.getPropertyValue('--safe-area-inset-top')) || 0,
    right: Number.parseFloat(styles.getPropertyValue('--safe-area-inset-right')) || 0,
    bottom: Number.parseFloat(styles.getPropertyValue('--safe-area-inset-bottom')) || 0,
    left: Number.parseFloat(styles.getPropertyValue('--safe-area-inset-left')) || 0,
  };
};

/**
 * Check if device has notch/pinhole camera
 */
export const hasNotch = (): boolean => {
  const insets = getSafeAreaInsets();
  return insets.top > 0;
};

/**
 * Check if device has system navigation bar at bottom
 */
export const hasSystemNavBar = (): boolean => {
  const insets = getSafeAreaInsets();
  return insets.bottom > 0;
};

/**
 * Re-apply the status bar configuration after something else has clobbered it.
 *
 * Entering native fullscreen (e.g. a <video> going immersive) lets Android drop
 * the window inset config set by configureStatusBar's setOverlaysWebView. On
 * exit the WebView can end up extending under the status bar, and because
 * env(safe-area-inset-*) reports 0 in the Android WebView there is no padding to
 * compensate — the header then overlaps the status bar. Re-applying the config
 * on fullscreen exit and on app resume restores it.
 */
export const watchForStatusBarReset = (): (() => void) => {
  if (!Capacitor.isNativePlatform() || typeof document === 'undefined') {
    return () => {};
  }

  const reapply = () => {
    configureStatusBar().catch((err) =>
      console.warn('StatusBar re-configuration failed:', err)
    );
  };

  // Only the exit transition matters; re-applying while still fullscreen would
  // show the status bar over the video.
  const onFullscreenChange = () => {
    if (!document.fullscreenElement) {
      reapply();
    }
  };

  document.addEventListener('fullscreenchange', onFullscreenChange);
  // Safari/older WebKit WebViews don't fire the unprefixed event.
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);

  let removeResume: (() => void) | undefined;
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      reapply();
    }
  })
    .then((listener) => {
      removeResume = listener.remove;
    })
    .catch((err) => console.warn('App state listener failed:', err));

  return () => {
    document.removeEventListener('fullscreenchange', onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    removeResume?.();
  };
};

/**
 * Initialize safe area handling on app start
 */
export const initializeSafeArea = async (): Promise<void> => {
  // Configure status bar
  await configureStatusBar();

  // Set CSS variables for safe area insets (fallback if env() not supported)
  const updateSafeAreaVariables = () => {
    const root = document.documentElement;

    // These are set by the browser/webview automatically
    // This is just a utility to access them
    const insets = getSafeAreaInsets();

    root.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
    root.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
    root.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
    root.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
  };

  // Update on init
  updateSafeAreaVariables();

  // Update on orientation change or resize
  if (typeof window !== 'undefined') {
    window.addEventListener('orientationchange', updateSafeAreaVariables);
    window.addEventListener('resize', updateSafeAreaVariables);
  }
};

const safeAreaDetector = {
  configureStatusBar,
  syncStatusBarTheme,
  getSafeAreaInsets,
  hasNotch,
  hasSystemNavBar,
  initializeSafeArea,
  watchForStatusBarReset,
};

export default safeAreaDetector;
